import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { stripeAPI } from '../../services/api';
import styled from 'styled-components';
import { FiCheckCircle, FiLoader, FiXCircle, FiArrowRight } from 'react-icons/fi';

// Type assertion for icons
const CheckCircleIcon = FiCheckCircle as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const LoaderIcon = FiLoader as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const XCircleIcon = FiXCircle as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const ArrowRightIcon = FiArrowRight as unknown as React.FC<React.SVGProps<SVGSVGElement>>;

const Container = styled.div`
  min-height: 100vh;
  background: var(--bg-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const Card = styled.div`
  background: white;
  border-radius: 24px;
  padding: 3rem;
  max-width: 600px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  text-align: center;
  animation: slideUp 0.5s ease-out;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const IconContainer = styled.div<{ status: 'loading' | 'success' | 'error' }>`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${props => 
    props.status === 'success' ? 'rgba(34, 197, 94, 0.1)' :
    props.status === 'error' ? 'rgba(239, 68, 68, 0.1)' :
    'rgba(59, 130, 246, 0.1)'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 2rem;
  
  svg {
    width: 40px;
    height: 40px;
    color: ${props => 
      props.status === 'success' ? '#22c55e' :
      props.status === 'error' ? '#ef4444' :
      '#3b82f6'};
    ${props => props.status === 'loading' && 'animation: spin 1s linear infinite;'}
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 1rem;
  font-family: 'Poppins', sans-serif;
  letter-spacing: -0.02em;
`;

const Message = styled.p`
  font-size: 1.1rem;
  color: #6b7280;
  margin-bottom: 2rem;
  line-height: 1.6;
  font-family: 'Poppins', sans-serif;
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 2rem;
  font-family: 'Poppins', sans-serif;
`;

const Button = styled.button`
  background: var(--accent-orange);
  color: white;
  border: none;
  padding: var(--button-padding, 0.4rem 0.75rem);
  border-radius: 12px;
  font-size: var(--button-font-size, 0.6rem);
  font-weight: 600;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;
  letter-spacing: -0.01em;
  white-space: nowrap;

  &:hover {
    background: var(--accent-orange-dark);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 150, 0, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const SubscriptionSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState<string>('Verifying your subscription...');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const verifySubscription = async () => {
      const sessionId = searchParams.get('session_id');
      
      if (!sessionId) {
        setStatus('error');
        setMessage('Invalid session');
        setError('No session ID provided. Please contact support if you completed payment.');
        return;
      }

      try {
        // Verify and process the checkout session
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/stripe/verify-session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ sessionId })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Failed to verify subscription');
        }

        if (data.processed) {
          setStatus('success');
          setMessage('Your subscription has been successfully activated!');
        } else {
          // Webhook will process it, but we can show success anyway
          setStatus('success');
          setMessage('Payment successful! Your subscription is being processed and will be active shortly.');
        }
      } catch (err: any) {
        console.error('Subscription verification error:', err);
        setStatus('error');
        setMessage('Verification failed');
        setError(err.message || 'Failed to verify your subscription. Please check your account settings or contact support.');
      }
    };

    verifySubscription();
  }, [searchParams]);

  const handleContinue = () => {
    if (user?.role === 'coach') {
      navigate('/coach/home');
    } else {
      navigate('/jumper/home');
    }
  };

  return (
    <Container>
      <Card>
        <IconContainer status={status}>
          {status === 'loading' && <LoaderIcon />}
          {status === 'success' && <CheckCircleIcon />}
          {status === 'error' && <XCircleIcon />}
        </IconContainer>
        
        <Title>
          {status === 'loading' && 'Processing...'}
          {status === 'success' && 'Payment Successful!'}
          {status === 'error' && 'Verification Failed'}
        </Title>
        
        <Message>{message}</Message>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        
        <Button onClick={handleContinue} disabled={status === 'loading'}>
          {status === 'success' ? 'Continue to Dashboard' : status === 'error' ? 'Go to Dashboard' : 'Processing...'}
          {status !== 'loading' && <ArrowRightIcon />}
        </Button>
      </Card>
    </Container>
  );
};

export default SubscriptionSuccess;

